import InfoIcon from "@mui/icons-material/Info";
import { Box, Divider, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import { controlKeys } from "../../../../api/queryKeys";
import CustomTooltip from "../../../../components/CustomTooltip";
import LoadingIcon from "../../../../components/LoadingIcon";
import ResizableDrawer from "../../../../components/ResizableDrawer";
import { DRAWER_TABS } from "../../../../utils/constants";
import ControlRemediationDrawer from "../../controls/components/ControlRemediationDrawer/ControlRemediationDrawer";
import NISTLabel from "../../controls/components/NISTLabel";
import PercentageLabel from "../../frameworks/components/PercentageLabel";
import TabbedMitigationControlsTable from "../../steps/components/TabbedMitigationControlsTable";
import { nistDescription } from "../nist_description";

const FunctionDrawer = ({ open, onClose, nistFunction, averageScore }) => {
  const colors = useColors();
  const { fetchAllControls, trackEvent } = useAPI();
  const [openDrawer, setOpenDrawer] = useState(false);
  const [selectedControl, setSelectedControl] = useState(null);

  const allCategoriesControls = new Set(
    nistFunction?.categories.flatMap((category) => category.controls)
  );

  const { data: controls, isLoading: controlsLoading } = useQuery({
    queryKey: controlKeys.all,
    queryFn: async () => {
      const controls = await fetchAllControls();
      return controls;
    },
    select: (response) => response.data,
    enabled: allCategoriesControls.size > 0,
  });

  const functionControls = useMemo(() => {
    return controls?.filter((control) =>
      allCategoriesControls.has(control.control_id)
    );
  }, [controls, allCategoriesControls]);

  const onRowClick = (control_id) => {
    setOpenDrawer(true);
    setSelectedControl(
      functionControls.find((control) => control.control_id === control_id)
    );

    trackEvent(
      "User clicked on a control " +
        control_id +
        " in the function drawer: " +
        nistFunction?.function
    );
  };

  const resetSelection = () => {
    setOpenDrawer(false);
    setSelectedControl(null);
  };

  return (
    <ResizableDrawer
      initialWidth={950}
      minWidth={950}
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        style: {
          borderRadius: "8px 0 0 0",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          padding: 3,
          gap: 3,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <NISTLabel nistFunction={nistFunction.function} size="medium" />
            <PercentageLabel
              applicable={averageScore >= 0}
              percentage={averageScore >= 0 ? averageScore : 0}
              sx={{ borderRadius: "10px", height: "24px" }}
            />
          </Box>
          <Typography variant="h5">{nistFunction.function}</Typography>
          <Typography variant="body1">
            {nistDescription[nistFunction.id]}
          </Typography>{" "}
          <Divider sx={{ mt: 1 }} />
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 1,
              alignItems: "center",
            }}
          >
            <Typography variant="subtitle1">Related Controls</Typography>
            <CustomTooltip
              title="Displays controls mapped to this function by status. Snoozed and excluded are counted as compliant in the overall function score. Click a row to view control details."
              placement="top"
            >
              <InfoIcon
                sx={{
                  width: "16px",
                  height: "16px",
                  color: colors.tooltip_info_icon,
                }}
              />
            </CustomTooltip>
          </Box>
          {!controlsLoading ? (
            functionControls?.length > 0 ? (
              <>
                {/* Table of associated controls */}
                <TabbedMitigationControlsTable
                  mitigatingControls={functionControls}
                  loading={controlsLoading}
                  onRowClick={onRowClick}
                  boxSXProps={{
                    border: "0px",
                  }}
                  filterColumns={[
                    "is_detected",
                    "control_id",
                    "name_for_gap_state",
                    "name_for_correct_state",
                    "tool",
                    "remediation_priority",
                    "affected_entity_counts_by_type",
                    "severity",
                  ]}
                />

                {/* Drawer to show selected control details */}
                <ControlRemediationDrawer
                  open={openDrawer}
                  onClose={resetSelection}
                  selectedTab={DRAWER_TABS.CONTROL_TAB}
                  selectedControl={selectedControl}
                />
              </>
            ) : (
              <Typography variant="subtitle2">
                No controls found for this function
              </Typography>
            )
          ) : (
            <LoadingIcon size={25} />
          )}
        </Box>
      </Box>
    </ResizableDrawer>
  );
};

export default FunctionDrawer;
